import { FC, useState } from "react";

import { AvailablePaymentCurrency } from "@simplyk/common";

import { BidHistoryEntry } from "../useAuctionDetailDrawer";

import { FormListBlock } from "@/canvas/features/FormV2/blocks/donor-list/FormListBlock";
import { FormListRow } from "@/canvas/features/FormV2/blocks/donor-list/FormListRow";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { Flash } from "@/icons/outlined";

const COLLAPSED_LIMIT = 5;

interface BidHistoryListProps {
  entries: BidHistoryEntry[];
  currency: AvailablePaymentCurrency;
  currentCommandIds?: readonly string[];
}

export const BidHistoryList: FC<BidHistoryListProps> = ({ entries, currency, currentCommandIds }) => {
  const { t } = useTranslate();
  const [expanded, setExpanded] = useState(false);

  const hasMore = entries.length > COLLAPSED_LIMIT;
  const visibleEntries = expanded ? entries : entries.slice(0, COLLAPSED_LIMIT);

  const formatName = (entry: BidHistoryEntry): string | null => {
    const isCurrentUser = entry.commandId && currentCommandIds?.includes(entry.commandId);
    if (isCurrentUser) {
      return entry.bidderName
        ? `${entry.bidderName} (${t("ticketing", "auctionV2.drawer.you")})`
        : t("ticketing", "auctionV2.drawer.you");
    }
    return entry.bidderName;
  };

  return (
    <FormListBlock
      title={t("ticketing", "auctionV2.drawer.bidHistory")}
      showViewAllButton={hasMore && !expanded}
      viewAllLabel={t("ticketing", "auctionV2.drawer.viewAll")}
      onViewAllClick={() => setExpanded(true)}
    >
      {visibleEntries.map((entry, index) => (
        <FormListRow
          key={index}
          fullName={formatName(entry)}
          date={new Date(entry.createdAtUtc)}
          amountInCents={entry.amount}
          currency={currency}
          anonymousLabel={t("ticketing", "auctionV2.drawer.anonymous")}
          trailingIcon={
            entry.isAutoBid ? (
              <FormIcon size="small" sx={(theme) => ({ color: theme.palette.forms?.text.brand })}>
                <Flash />
              </FormIcon>
            ) : undefined
          }
          iconPosition="before"
          iconTooltip={entry.isAutoBid ? t("ticketing", "auctionV2.drawer.autoBidTooltip") : undefined}
        />
      ))}
    </FormListBlock>
  );
};
